import type { Product, SubProduct, ProductConditionParams } from '@nf/types/product';
import { thirdPartyProducts } from '@/settings/products/third-party-products';
import { useAuthToken } from '@/store/auth';
import { homePageStyle as style } from '../home-page.style';
import { useEffect, useState, useMemo } from 'react';
import { useAccountInfo } from '@/hooks/query/account';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { SwiperContainer } from '@/components/containers/swiper-container';
import { useTranslations } from 'next-intl';
import { triggerRewardSystemPanel } from '@nf/achievement-system-v2';
import { ProductProps } from '@/features/home-page/products';
import { isNumberGames, getForbiddenSport, sortVirtualSports } from '@/services/settings.service';
import { sportService } from '@/services/sport.service';
import { EventMode, RawMenuData } from '@/types/menu-setting';
import clsx from 'clsx';
import { homeStyle } from './home-page.style';
import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { useAppearanceStore } from '@/store/appearance';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { ViewEnum } from '@/types';
import { useCasinoRoyale } from '@/hooks/query/use-casino-royale';
import { useMenuTypeList, useOriginMenuData } from '@/hooks/query';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { useSiteStore } from '@/store/site';
import { sportsbookService } from '@/services/sportsbook.service';
import { CdnImg } from '@/features/cdn-img';
import { useTemplateResourceContext } from '../context/template-resource-context';
import { HomePageProduct } from '../types/products';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { OpenTypeEnum } from '@/types/template-resource';
import { modalService } from '@/services/modal.service';
import { NonSportProductProps } from '../products-wide';
import { siteService } from '@/services/site.service';
import { AthenaSiteNameEnums } from '@nf/types/site';
import { TabSwitch } from '@/components/widgets/tab-switch';
import { useMemoizeArgs } from '@/jupiter/hooks/use-memo-args';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { openAdvantPlay } from '@/services/third-party-entrance.service';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { SINGLE_SPORT_MENU_MODE } from '@/constants/sport-menu-mode';

const productTypeList = [
	{ Label: 'Sports', IconName: 'sports' },
	{ Label: 'Games', IconName: 'casino' }
];

export const useRewardSystemTrigger = () => {
	const { user } = useAccountInfo();
	const triggerWithCare = (event?: string) => {
		if (user?.EnableRewardMaintenance) {
			const event = new CustomEvent('open-um');
			document.dispatchEvent(event);
		} else {
			if (!event) {
				triggerRewardSystemPanel();
			} else {
				document.dispatchEvent(new CustomEvent(event))
			}
		}
	};
	return {
		triggerWithCare
	}
}
const AccountInfo = () => {
	const authToken = useAuthToken();
	const { user } = useAccountInfo();
	const { triggerWithCare } = useRewardSystemTrigger();
	const { SiteConfigsData } = useGetSiteConfigs();
	const displayCurrencyName = SiteConfigsData?.EnableCurrencyDisplay ? user?.Curr : '';


	const handleClick = () => {
		if (user?.RewardInfo?.EnableRewardSystem) {
			const kafkaData = {
				note: KafkaNote.Reward,
				type: 1,
				action: 33
			}
			setKafkaNormal(kafkaData);
		}
		triggerWithCare();
	}

	return (
		!authToken ? null :
			!user?.RewardInfo?.EnableRewardSystem ? (
				<div
					className="home-account"
					onClick={handleClick}
				>
					<div className="home-account__info">
						<div className={clsx('home-account__name', homeStyle.username)}>{user?.DisplayUserName}</div>
						<div className={clsx('home-account__balance', homeStyle.balance)}>{`${displayCurrencyName} ${user?.BCredit}`}</div>
					</div>
				</div>
			) : (
				<div
					className="home-account"
					data-level={user?.RewardInfo.VipLevel}
					onClick={handleClick}
				>
					<div className="vip-badge" data-level={user?.RewardInfo.VipLevel} />
					<div className="home-account__info">
						<div className="home-account__name">{user?.DisplayUserName}</div>
						<div className="home-account__balance">{`${displayCurrencyName} ${user?.BCredit}`}</div>
					</div>
				</div>
			)
	);
};

type OptionsProps = {
	availableEventModes: EventMode[],
	setThirdPartyProduct: (products: ProductProps[]) => void,
	setCurrentEventMode: (eventMode: EventMode) => void,
	setIsContributor: (contributor: boolean) => void,
	setNonSportProducts: (nonSportProductProps: NonSportProductProps[]) => void,
};

export const MobileOptions = ({ availableEventModes, setThirdPartyProduct, setCurrentEventMode, setIsContributor, setNonSportProducts }: OptionsProps) => {
	const router = useRouter();
	const locale = useLocale();
	const t = useTranslations('General');
	const { filterSabaString } = useFilterSabaString();
	const { SiteConfigsData } = useGetSiteConfigs();
	const { openPredictionMarket, canSeePredictionMarket } = usePredictionMarket();
	const { openCasinoRoyale } = useCasinoRoyale();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const { user } = useAccountInfo();
	const [currentProduct, setCurrentProduct] = useState<string>('');
	const [products, setProducts] = useState<Product[]>([]);
	const templateResourceContext = useTemplateResourceContext();
	const { productType, setProductType, productId, setProductId, templateResource, isSuccess } = templateResourceContext;
	const [homePageProducts, setHomePageProducts] = useState<Array<HomePageProduct>>([]);
	const [selectedProduct, setSelectedProduct] = useState<number | undefined>(productId);
	const { menuTypeList } = useMenuTypeList();

	const view = useAppearanceStore(state => state.view);
	const oddsPageLayout = useAppearanceStore(state => state.oddsPageLayout);
	const isNewbie = oddsPageLayout === 'list';
	const [
		setIsOpenExchange,
		deepLinkSiteInfo
	] = useSiteStore(state => [
		state.setIsOpenExchange,
		state.deepLinkSiteInfo
	]);
	const forbiddenSport = getForbiddenSport(view);
	const { menuResponse } = useOriginMenuData({ isNeedParlayData: false });
	const [available3rdPartyGames, setAvailable3rdPartyGames] = useState<Array<number>>([]);
	const { enableESoccerBattle, nonSport } = useLicSettings();

	const productConditionParams = useMemo((): ProductConditionParams => ({
		available3rdPartyGames,
		virtualSoccerList: nonSport?.virtualSoccerList,
		canSeeCasinoRoyale: SiteConfigsData?.CanSeeCasinoRoyale === true,
		canSeePredictionMarket: canSeePredictionMarket === true,
		enableSabaScore: SiteConfigsData?.EnableSabaScore === true,
		forbiddenSport,
		isEuroCupEnabled: availableEventModes.indexOf('M3') !== -1,
		isNewbie,
		enableESoccerBattle: enableESoccerBattle ?? false,
		siteId: SiteConfigsData?.GetMessageID ?? '0',
		user,
		canSeeAdvantPlayVendorLobby: Boolean(user?.CanSeeAdvantPlayVendorLobby || SiteConfigsData?.CanSeeAdvantPlayVendorLobby),
	}), [
		available3rdPartyGames,
		nonSport?.virtualSoccerList,
		SiteConfigsData?.CanSeeCasinoRoyale,
		canSeePredictionMarket,
		SiteConfigsData?.EnableSabaScore,
		forbiddenSport,
		availableEventModes,
		isNewbie,
		enableESoccerBattle,
		SiteConfigsData?.GetMessageID,
		user,
		SiteConfigsData?.CanSeeAdvantPlayVendorLobby,
	]);

	const checkProductEnabled = (product: Product | SubProduct): boolean => {
		if (product.enabled !== undefined) {
			return product.enabled;
		}

		if (product.subProducts) {
			return product.subProducts.some(
				subProduct => subProduct.enableCondition?.(productConditionParams)
			);
		}

		return product.enableCondition?.(productConditionParams) ?? false;
	};

	useEffect(() => {
		const currentAvailable3rdPartyGames: Array<number> = menuResponse.reduce((available3rdPartyGames: Array<number>, rawMenuData: RawMenuData): Array<number> => {
			const { GameId } = rawMenuData;
			if ((isNumberGames(GameId) || sportService.isVirtualSports(GameId)) && !available3rdPartyGames.includes(GameId)) {
				available3rdPartyGames.push(GameId);
			}
			return available3rdPartyGames;
		}, []);
		if (available3rdPartyGames.length !== currentAvailable3rdPartyGames.length || currentAvailable3rdPartyGames.some(currentAvailable3rdPartyGame => !available3rdPartyGames.includes(currentAvailable3rdPartyGame))) {
			setAvailable3rdPartyGames(currentAvailable3rdPartyGames);
		}

	}, [menuResponse]);

	useEffect(() => {
		setProducts(
			availableEventModes.length ? [
				...availableEventModes
					.filter(menuType => !['M27', 'M28'].includes(menuType))		// M27, M28 dont show on lobby
					.map(menuType => {
						const modeId = menuType.replace(/^M(\d+)$/, '$1');
						const isAllSports = modeId === '0';
						return {
							productName: `M${modeId}`,
							image: {
								href: sportService.getHomeEventModeIcon(modeId),
							},
							resourceKey: isAllSports 
								? 'lbl_Sports' 
								: sportsbookService.makeMenuNodeResourceKey(SiteConfigsData?.EnableFilterSaba, modeId),
						};
					}),
				...thirdPartyProducts.filter(checkProductEnabled)
			] : []
		);
	}, [availableEventModes, productConditionParams]);

	useEffect(() => {
		if (products.length > 0) {
			if (!currentProduct || products.every(existingProduct => existingProduct.productName !== currentProduct)) {
				const sort = templateResource.ProductResources.find(productResource => productResource.OpenType == OpenTypeEnum.Inner)?.Sort;
				if (sort) {
					const index = templateResource.ProductResources.map(productResource => productResource.Sort).indexOf(sort);
					onOptionClick(index);
				}
			}
		}
	}, [currentProduct, products]);

	const onOptionClick = (index: number) => {
		if (!isSuccess || homePageProducts.length === 0 || (homePageProducts[index] && homePageProducts[index].OpenType === OpenTypeEnum.Popup)) {
			return;
		} 
		const selectedOption = products.find(product => 
			product.productName === homePageProducts[index]?.Id ||
			product.productName.toLowerCase() === homePageProducts[index]?.Name?.replace(' ', '').toLowerCase()
		);
		if (selectedOption) {
			const optionName = selectedOption.productName;
			setKafka(optionName);
			setCurrentProduct(optionName);

			if (selectedOption.subProducts) {
				const visibleProductProps = selectedOption.subProducts.filter(checkProductEnabled);

				if (visibleProductProps.length > 0) {
					// virtual sports 有項目才顯示 VSTournament，並顯示在第一順位
					if (selectedOption.productName === 'virtualSports' && user?.VSTournament && user?.ActStatus == 2) {
						visibleProductProps.unshift({
							productName: 'vsTournament',
							GameId: 'virtual-tournament',
							GlobalResourceLabel: 'lbl_VSTournament',
							RouteTarget: '/third-party/vs-tournament'
						});
					}

					// vs2 tournament sort
					const sortedVisibleProductProps = sortVirtualSports(visibleProductProps.map(product => ({ GameId: product.GameId })))
						.map(sortedItem => visibleProductProps.find(product => product.GameId === sortedItem.GameId)!);
					
					setThirdPartyProduct(
						sortedVisibleProductProps.map(productProps => ({
							...productProps,
							GlobalResourceLabel: t(productProps.GlobalResourceLabel)
						}))
					);
				}
			}
		} else {
			setThirdPartyProduct([]);
			setNonSportProducts([]);
		}
	}

	const setKafka = (optionName: string) => {
		if (view !== ViewEnum.mars || window.innerWidth >= 750) return; // 只計 mars 且寬度小於 750
	
		let action;
		switch (optionName) {
			case 'exchange':
				action = 2;
				break;
			case 'casinoRoyale':
				action = 3;
				break;
			case 'sabaScore':
				action = 4;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	useEffect(() => {
		if (isSuccess && products.length > 0) {
			getHomePageProducts();
			const Id = templateResource.ProductResources.find(productResource => productResource.OpenType == OpenTypeEnum.Inner)?.Sort;
			if (Id) {
				const index = templateResource.ProductResources.map(productResource => productResource.Sort).indexOf(Id);
				setSelectedProduct(index);
				onOptionClick(index);
			}
		}
	}, [isSuccess, products, useMemoizeArgs(templateResource?.ProductResources)]);

	const getHomePageProducts = () => {
		if (templateResource.ProductResources) {
			setHomePageProducts([]);
			const productElements: Array<HomePageProduct> = [];
			templateResource.ProductResources.map(item => {
				if (item.TheURL?.toLowerCase() == 'sports') {
					products
						.filter(product => /^M\d+$/.test(product.productName))
						.map(product => {
							productElements.push({
								Id: product.productName,
								Name: filterSabaString(t(product.resourceKey)),
								ClassName: product.image.href,
								OpenType: 0,
								TheURL: '',
								SiteConfigKey: item.SiteConfigKey
							})
						});
				} else {
					productElements.push({
						Name: item.Name,
						ClassName: `${SiteConfigsData?.ImageServerUrl}${item.ClassName}`,
						OpenType: item.OpenType,
						TheURL: item.TheURL,
						SiteConfigKey: item.SiteConfigKey
					});
				}
			});
			setHomePageProducts(productElements);
		}
	}


	const clickEvent = (url?: string) => {
		if (isBefore) {
			switch (url?.toLowerCase()) {
				case 'vgaming':
				case 'score':
				case 'arcadia':
				case 'advantplay':
				case 'predictionmarket':
					modalService.openLoginModal();
					return;
				default:
					return;
			}
		} else {
			switch (url?.toLowerCase()) {
				case 'vgaming':
					openCasinoRoyale({ sportTypeId: '171', betTypeId: '0', isApp: deepLinkSiteInfo.isApp });
					break;
				case 'arcadia':
					openCasinoRoyale({ sportTypeId: '165', isApp: deepLinkSiteInfo.isApp });
					break;
				case 'advantplay':
					openAdvantPlay({ isApp: deepLinkSiteInfo.isApp }, authToken ?? undefined);
					break;
				case 'score':
					router.push('/score', { locale })
					break;
				case 'predictionmarket':
					openPredictionMarket();
					break;
				default:
					return;
			}
		}
	}

	useEffect(() => {
		if (isSuccess && selectedProduct !== undefined) {
			const resource = templateResource.ProductResources.find(productResource => productResource.Name === homePageProducts[selectedProduct]?.Name);
			const selectedProductItem = homePageProducts[selectedProduct];
			if (selectedProductItem?.Id !== undefined && /^M\d+$/.test(selectedProductItem.Id)) {
				const eventMode = selectedProductItem.Id as EventMode;
				setCurrentEventMode(eventMode);
				setProductId(undefined);
			} else if (resource && resource.OpenType == OpenTypeEnum.Inner) {
				setProductId(resource.Id);
			} else {
				setCurrentEventMode('M0' as EventMode);
			}
		}
	}, [selectedProduct, isSuccess]);

	useEffect(() => {
		const isContributorTemp = (selectedProduct !== undefined && Boolean(products.find(product => product.productName === homePageProducts[selectedProduct]?.Id ?homePageProducts[selectedProduct].Id: '')));
		setIsContributor(
			isContributorTemp
		)
	}, [selectedProduct, products, homePageProducts]);

	return (
		<>
			{siteService.isMatchingSite(SiteConfigsData?.GetMessageID, AthenaSiteNameEnums.Macaubet) ? (
				<TabSwitch
					className={clsx('tab-switch', style.tabSwitch)}
					tabs={productTypeList.map((productType) => productType.Label)}
					selectedIndex={productTypeList.map(prodcutType => prodcutType.Label).indexOf(productType?productType:'')}
					onTabChange={
						(index) => {							
							setProductType(productTypeList[index].Label);
						}
					}
				/>
			) : null}
			<div className="home-option">
				<AccountInfo />
				{
					isSuccess && 
					<SwiperContainer
						slideElements={homePageProducts.map((product) => {
							const baseHref = product.ClassName;
							return (
								<>
									<CdnImg
										key={product.Name}
										className="home-nav__icon"
										src={baseHref ? baseHref: ''}
										onClick={() => clickEvent(product.TheURL)}
									/>
									<div className="home-nav__title">{product.Name}</div>
								</>
							)
						})}
						disableOnClickIdx={homePageProducts.map((product, index) => { 
							if (product.OpenType === OpenTypeEnum.Popup) {
								return index;
							} return undefined;
						}).filter((index): index is number => index !== undefined)}
						className="home-nav"
						elementClassName={clsx('home-nav__item', homeStyle.navItem)}
						onSlideClick={(index: number) => {
							const prevIndex = selectedProduct;
							if (SINGLE_SPORT_MENU_MODE.includes(homePageProducts[index].Id as EventMode)) {
								const modeId = Number(homePageProducts[index].Id?.replace(/^M(\d+)$/, '$1'));
								const path = menuTypeList.find(item => item.modeId === modeId)?.path;
								if (path) {
									if (modeId === 50 && view === ViewEnum.galaxy) {
										setKafkaNormal({
											note: KafkaNote.GalaxyV2,
											type: 16,
											action: 3
										});
									} else if (modeId === 50 && view === ViewEnum.mars) {
										setKafkaNormal({
											note: KafkaNote.MarsV1,
											type: 1,
											action: 26
										});
									}
									router.push(path, { locale });
								}
								return;
							}
							setSelectedProduct(index);
							const currentProudct = homePageProducts[index];
							if (currentProudct.SiteConfigKey === 'CanSeeVirtualSports') {
								wcPromotionMissionService.notifyMission({
									taskType: TaskTypeEnum.ClickVirtualSports,
									productCode: 'virtualSports'
								});
							}
							switch (currentProudct.OpenType) {
								case OpenTypeEnum.Inner:
									break;
								case OpenTypeEnum.Popup:
									if (prevIndex !== undefined) {
										setSelectedProduct(prevIndex);
									} else {
										setSelectedProduct(0);
									}
									break;
								default:
									break;
							}
							onOptionClick(index);
						}}
						currentIndex={selectedProduct}
						customProps={{
							navigation: true,
							allowTouchMove: true,
							spaceBetween: 3,
							breakpoints: {
								750: {
									spaceBetween: 12,
									allowTouchMove: false
								}
							}
						}}
					/>
				}
			</div>
		</>
	);
};